<template>
    <div>
        <!-- Full page loader -->
        <div
            v-if="isLoading"
            class="fixed inset-0 z-50 bg-white/10 backdrop-blur-sm flex items-center justify-center"
        >
            <div class="h-12 w-12 border-4 border-orange-500 border-dashed rounded-full animate-spin"></div>
        </div>

        <!-- App content wrapper (disabled while loading) -->
        <div :class="{ 'pointer-events-none select-none opacity-50': isLoading }">
            <div
                class="block sm:flex min-h-screen bg-white"
                :class="cmsView ? '' : 'flex-col'"
            >
                <!-- Sidebar only if cmsView is true -->
                <Sidebar
                    v-if="cmsView"
                    ref="sidebar"
                    :menu-groups="menuGroups"
                    :is-open="isSidebarOpen"
                    :collapsed="sidebarCollapsed"
                />

                <div
                    class="flex flex-col"
                    :class="cmsView ? 'flex-grow' : ''"
                >
                    <!-- Commented for making the nav bar transparent at top in dashboard -->
                    <!-- <Header
                        class="z-20 bg-white shadow p-3 md:px-3 lg:px-8 sticky top-0 left-0"
                        :sidebar-status="isSidebarOpen"
                        @toggleSidebarUnified="handleSidebarToggle"
                    /> -->
                    <Header
                        :class="[
                            isDashboard
                                ? 'absolute top-0 left-0 w-full z-40 bg-transparent text-white'
                                : 'sticky top-0 left-0 z-20 bg-white shadow',
                            'p-3 md:px-3 lg:px-8 transition-colors duration-300',
                        ]"
                        :sidebar-status="isSidebarOpen"
                        @toggleSidebarUnified="handleSidebarToggle"
                    />

                    <!-- Page Content  [Home page without login] -->
                    <!-- <main
                        class="overflow-x-visible flex-grow min-h-screen bg-gray-50"
                        :class="[!isDashboard && 'lg:px-4']"
                    > -->
                    <!-- Main content -->
                    <main
                        class="flex-grow overflow-x-visible min-h-[84vh] bg-gray-50 dark:bg-gray-800"
                        :class="[isDashboard ? 'relative' : '', !isDashboard && 'lg:px-4']"
                    >
                        <slot />
                    </main>

                    <Footer />
                </div>
            </div>

            <FlashMessages />

            <!-- Notify -->
            <portal-target
                name="notify"
                multiple
            />

            <KycAlert />

            <!-- Modal Portal -->
            <portal-target
                name="modal"
                multiple
            />
        </div>
    </div>
</template>

<script>
    import Header from './Header.vue';
    import Footer from './Footer.vue';
    import FlashMessages from '../Components/FlashMessages.vue';
    import KycAlert from '../Components/KycAlert.vue';
    import Sidebar from './Sidebar.vue';

    import HomeIcon from '../Components/Icons/HomeIcon.vue';
    import DiscountIcon from '../Components/Icons/DiscountIcon.vue';
    import BrandIcon from '../Components/Icons/BrandIcon.vue';
    import PeopleIcon from '../Components/Icons/PeopleIcon.vue';
    import BankIcon from '../Components/Icons/BankIcon.vue';
    import ChatIcon from '../Components/Icons/ChatIcon.vue';
    import CategoryIcon from '../Components/Icons/CategoryIcon.vue';
    import DashboardIcon from '../Components/Icons/DashboardIcon.vue';
    import ProductIcon from '../Components/Icons/ProductIcon.vue';
    import WholesalerIcon from '../Components/Icons/WholesalerIcon.vue';
    import ReportInvoiceIcon from '../Components/Icons/ReportInvoiceIcon.vue';
    import ReportIcon from '../Components/Icons/ReportIcon.vue';
    import ReportRetailerIcon from '../Components/Icons/ReportRetailerIcon.vue';
    import StockIcon from '../Components/Icons/StockIcon.vue';
    import PackedIcon from '../Components/Icons/PackedIcon.vue';
    import PlacedIcon from '../Components/Icons/PlacedIcon.vue';
    import ShippedIcon from '../Components/Icons/ShippedIcon.vue';
    import DeliveredIcon from '../Components/Icons/DeliveredIcon.vue';
    import CashIcon from '../Components/Icons/CashIcon.vue';
    import ChequeIcon from '../Components/Icons/ChequeIcon.vue';
    import ChequeBounceIcon from '../Components/Icons/ChequeBounceIcon.vue';
    import ImageIcon from '../Components/Icons/ImageIcon.vue';
    import CloseIcon from '../Components/Icons/CloseIcon.vue';
    import ErrorLogIcon from '../Components/Icons/ErrorLogIcon.vue';
    import WarningIcon from '../Components/Icons/WarningIcon.vue';

    export default {
        props: {
            loading: {
                type: Boolean,
                default: false,
            },
        },

        components: {
            Header,
            FlashMessages,
            Footer,
            Sidebar,
            KycAlert,

            HomeIcon,
            DashboardIcon,
            CategoryIcon,
            ProductIcon,
            WholesalerIcon,
            DiscountIcon,
            BrandIcon,
            ChatIcon,
            ReportIcon,
            ReportInvoiceIcon,
            ReportRetailerIcon,
            StockIcon,
            PackedIcon,
            PlacedIcon,
            ShippedIcon,
            DeliveredIcon,
            CashIcon,
            ChequeIcon,
            ChequeBounceIcon,
            ImageIcon,
            CloseIcon,
            BankIcon,
            PeopleIcon,
            ErrorLogIcon,
            WarningIcon,
        },

        data() {
            return {
                //isSidebarOpen: this.cmsView && !this.$page.url.includes('/products/'), // whether the sidebar is *shown*
                sidebarCollapsed: localStorage.getItem('sidebarCollapsed') === 'true', // icon-only
                localLoading: false, // ← safe internal control
            };
        },

        mounted() {
            this.$inertia.on('start', () => {
                this.localLoading = true;
            });
            this.$inertia.on('finish', () => {
                this.localLoading = false;
            });
        },

        computed: {
            // todo: might want to replace this function logic with a regex
            // isProductShowPage() {
            //     return this.$page.url.includes('/products/');
            // },
            isLoading() {
                return this.loading || this.localLoading;
            },
            isSidebarOpen() {
                return this.cmsView && !this.$page.url.includes('/products/');
            },
            menuGroups() {
                const user = this.$page.props.user;
                if (!user) return [];

                const all = {
                    main: [
                        {
                            name: this.$t('MENU.LBL_DASHBOARD'),
                            icon: HomeIcon,
                            fill: '#FFFFFF',
                            routeName: 'dashboard',
                        },
                        {
                            name: this.$t('MENU.LBL_PRODUCTS'),
                            icon: ProductIcon,
                            fill: '#FFFFFF',
                            routeName: 'products.index',
                        },
                        {
                            name: this.$t('MENU.LBL_CATEGORIES'),
                            icon: CategoryIcon,
                            fill: '#FFFFFF',
                            routeName: 'categories.index',
                        },
                        {
                            name: this.$t('MENU.LBL_BRANDS'),
                            icon: BrandIcon,
                            fill: '#FFFFFF',
                            routeName: 'brands.index',
                        },
                    ],
                    management: [
                        {
                            name: this.$t('MENU.LBL_USER_MANAGEMENT'),
                            icon: PeopleIcon,
                            fill: '#FFFFFF',
                            routeName: 'users.management.index',
                        },
                        {
                            name: this.$t('MENU.LBL_RETAILER_MANAGEMENT'),
                            icon: PeopleIcon,
                            fill: '#FFFFFF',
                            routeName: 'users.management.retailer.index',
                        },
                        {
                            name: this.$t('MENU.LBL_ASSIGNED_RETAILERS'),
                            icon: DiscountIcon,
                            fill: '#FFFFFF',
                            routeName: 'users.management.retailer.assigned',
                        },
                        {
                            name: this.$t('MENU.LBL_LOCATION_LOGS'),
                            icon: ChatIcon,
                            fill: '#FFFFFF',
                            routeName: 'location-logs.index',
                        },
                        {
                            name: this.$t('MENU.LBL_CHAT'),
                            icon: ChatIcon,
                            fill: '#FFFFFF',
                            routeName: 'chat.index',
                        },
                        {
                            name: this.$t('MENU.LBL_ADMIN_CHAT'),
                            icon: ChatIcon,
                            fill: '#FFFFFF',
                            routeName: 'admin.chat.index',
                        },
                    ],
                    orders: [
                        {
                            name: this.$t('MENU.LBL_NEW_ORDERS'),
                            icon: PlacedIcon,
                            fill: '#FFFFFF',
                            routeName: 'bills.placed',
                        },
                        {
                            name: this.$t('MENU.LBL_PACKED_ORDERS'),
                            icon: PackedIcon,
                            fill: '#FFFFFF',
                            routeName: 'bills.packed',
                        },
                        {
                            name: this.$t('MENU.LBL_SHIPPED_ORDERS'),
                            icon: ShippedIcon,
                            fill: '#FFFFFF',
                            routeName: 'bills.shipped',
                        },
                        {
                            name: this.$t('MENU.LBL_DELIVERED_ORDERS'),
                            icon: DeliveredIcon,
                            fill: '#FFFFFF',
                            routeName: 'bills.delivered',
                        },
                    ],
                    reports: [
                        {
                            name: this.$t('MENU.LBL_STOCKS'),
                            icon: StockIcon,
                            fill: '#FFFFFF',
                            routeName: 'stockchange.index',
                        },
                        {
                            name: this.$t('MENU.LBL_INVOICE_REPORT'),
                            icon: ReportInvoiceIcon,
                            fill: '#FFFFFF',
                            routeName: 'reports.invoice',
                        },
                        {
                            name: this.$t('MENU.LBL_RETAILER_REPORT'),
                            icon: ReportRetailerIcon,
                            fill: '#FFFFFF',
                            routeName: 'reports.retailer',
                        },
                        {
                            name: this.$t('MENU.LBL_WHOLESALER_REPORT'),
                            icon: ReportIcon,
                            fill: '#FFFFFF',
                            routeName: 'reports.wholesaler',
                        },
                    ],
                    payments: [
                        {
                            name: this.$t('MENU.LBL_PAYMENT_COLLECTION'),
                            icon: CashIcon,
                            fill: '#FFFFFF',
                            routeName: 'payment.collection',
                        },
                        {
                            name: this.$t('MENU.LBL_CHEQUE_PAYMENTS'),
                            icon: ChequeIcon,
                            fill: '#FFFFFF',
                            routeName: 'payment.cheque',
                        },
                        {
                            name: this.$t('MENU.LBL_BOUNCE_CHEQUES'),
                            icon: ChequeBounceIcon,
                            fill: '#FFFFFF',
                            routeName: 'payment.cheque.bounced',
                        },
                    ],
                    configuration: [
                        {
                            name: this.$t('MENU.LBL_FEATURED_IMAGES'),
                            icon: ImageIcon,
                            fill: '#FFFFFF',
                            routeName: 'featured.index',
                        },
                        {
                            name: this.$t('MENU.LBL_PRODUCT_FEATURED_IMAGES'),
                            icon: ImageIcon,
                            fill: '#FFFFFF',
                            routeName: 'productFeaturedImages.index',
                        },
                        {
                            name: this.$t('MENU.LBL_PROMOCODES'),
                            icon: DiscountIcon,
                            fill: '#FFFFFF',
                            routeName: 'promocodes.index',
                        },
                        { name: this.$t('MENU.LBL_BANKS'), icon: BankIcon, fill: '#FFFFFF', routeName: 'banks.index' },
                    ],
                    logs: [
                        {
                            name: this.$t('MENU.LBL_ERROR_LOG'),
                            icon: ErrorLogIcon,
                            fill: '#FFFFFF',
                            routeName: 'logs.errors',
                        },
                        {
                            name: this.$t('MENU.LBL_ACTIVITY_LOG'),
                            icon: WarningIcon,
                            fill: '#FFFFFF',
                            routeName: 'logs.activities',
                        },
                    ],
                };

                // Role-based filtering
                const groupFilters = {
                    main: () => {
                        if (this.isAdmin) return all.main;
                        return all.main.filter(
                            (i) => ![this.$t('MENU.LBL_CATEGORIES'), this.$t('MENU.LBL_BRANDS')].includes(i.name),
                        );
                    },
                    management: () => {
                        if (this.isAdmin)
                            return all.management.filter(
                                (i) =>
                                    i.name !== this.$t('MENU.LBL_ASSIGNED_RETAILERS') &&
                                    i.name !== this.$t('MENU.LBL_CHAT'),
                            );
                        if (this.isWholesaler)
                            return all.management.filter(
                                (i) =>
                                    ![this.$t('MENU.LBL_ADMIN_CHAT'), this.$t('MENU.LBL_ASSIGNED_RETAILERS')].includes(
                                        i.name,
                                    ),
                            );
                        if (this.isSaleperson)
                            return all.management.filter(
                                (i) =>
                                    ![
                                        this.$t('MENU.LBL_USER_MANAGEMENT'),
                                        this.$t('MENU.LBL_RETAILER_MANAGEMENT'),
                                        this.$t('MENU.LBL_ADMIN_CHAT'),
                                        this.$t('MENU.LBL_CHAT'),
                                    ].includes(i.name),
                            );
                        return [];
                    },
                    configuration: () => {
                        if (this.isAdmin) return all.configuration;
                        if (this.isWholesaler)
                            return all.configuration.filter(
                                (i) =>
                                    ![
                                        this.$t('MENU.LBL_FEATURED_IMAGES'),
                                        this.$t('MENU.LBL_PRODUCT_FEATURED_IMAGES'),
                                    ].includes(i.name),
                            );
                        return [];
                    },
                    orders: () => {
                        if (this.isWholesaler) return all.orders;
                        return []; // Customize filtering as needed
                    },
                    reports: () => {
                        if (this.isAdmin) return all.reports; // Customize filtering as needed
                        if (this.isWholesaler)
                            return all.reports.filter((i) => !i.name.includes(this.$t('MENU.LBL_WHOLESALER_REPORT')));
                        if (this.isSalesperson)
                            return all.reports.filter(
                                (i) =>
                                    ![this.$t('MENU.LBL_STOCKS'), this.$t('MENU.LBL_WHOLESALER_REPORT')].includes(
                                        i.name,
                                    ),
                            );
                        return [];
                    },
                    payments: () => {
                        if (this.isAdmin)
                            return all.payments.filter((i) => i.name !== this.$t('MENU.LBL_PAYMENT_COLLECTION'));
                        if (this.isWholesaler) return all.payments;
                        return [];
                    },
                    logs: () => {
                        if (this.isSuperAdmin) return all.logs;
                        return [];
                        // return all.logs;
                    },
                };

                return [
                    { title: this.$t('MENU_HEADER.LBL_MAIN'), items: groupFilters.main() },
                    { title: this.$t('MENU_HEADER.LBL_MANAGEMENT'), items: groupFilters.management() },
                    { title: this.$t('MENU_HEADER.LBL_ORDERS'), items: groupFilters.orders() },
                    { title: this.$t('MENU_HEADER.LBL_REPORTS'), items: groupFilters.reports() },
                    { title: this.$t('MENU_HEADER.LBL_PAYMENTS'), items: groupFilters.payments() },
                    { title: this.$t('MENU_HEADER.LBL_CONFIGURATION'), items: groupFilters.configuration() },
                    { title: this.$t('MENU_HEADER.LBL_LOGS'), items: groupFilters.logs() },
                ].filter((group) => group.items.length > 0);
            },
        },

        methods: {
            handleSidebarToggle() {
                if (window.innerWidth < 768) {
                    this.$refs.sidebar.toggleMobile(); // new method in Sidebar
                } else {
                    this.sidebarCollapsed = !this.sidebarCollapsed;
                    localStorage.setItem('sidebarCollapsed', this.sidebarCollapsed);
                }
            },
            // toggleSidebar() {
            //     if (window.innerWidth < 768) {
            //         // Mobile → show/hide the whole sidebar
            //         this.isSidebarOpen = !this.isSidebarOpen;
            //     } else {
            //         // Desktop → collapse/expand
            //         this.sidebarCollapsed = !this.sidebarCollapsed;
            //         localStorage.setItem('sidebarCollapsed', this.sidebarCollapsed);
            //     }
            // },
        },
    };
</script>

<style scoped>
    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }

        100% {
            transform: rotate(360deg);
        }
    }

    .animate-spin {
        animation: spin 1s linear infinite;
    }
</style>
